import { useXRErrorGuidance } from "@core/hooks/useXRErrorGuidance";
import { Spinner } from "@core/components/Elements";

import Wordmark from "../assets/MURMURATIONS.svg";
import Bg from "../assets/splash.webp";

interface SplashProps {
  isLoading: boolean;
  /** Legacy begin handler — unused in the auto-start flow, kept for App.tsx wiring. */
  onClose: () => void;
  onBack?: () => void;
  /** When true, shows "Starting AR..." once loading completes (App Clip auto-start). */
  autoStart?: boolean;
  /** Flock-download readout under the spinner (e.g. "62%" or "13.4 MB"). null = hide. */
  progressLabel?: string | null;
  /** 0..1 download fraction for the determinate bar; null = indeterminate (text only). */
  progressFraction?: number | null;
}

/**
 * AR-entry loading screen. Only the iOS App Clip path lands here — it
 * auto-starts the XR session as soon as content is ready, so this surface is
 * wordmark + spinner + XR error guidance over the City Hall photo.
 *
 * The web "Enter the Murmuration" splash is a different surface: see
 * EnterSplash.tsx, the microsite preload gate.
 */
export function Splash({ isLoading, onBack, autoStart, progressLabel, progressFraction }: SplashProps) {
  const { errorMessage } = useXRErrorGuidance();

  return (
    <div
      className="fixed inset-0 flex flex-col overflow-hidden pointer-events-auto z-50"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(21,11,42,0.0) 0%, rgba(21,11,42,0.55) 80%, rgba(21,11,42,0.9) 100%), url(${Bg})`,
        backgroundPosition: "center",
        backgroundSize: "cover",
        minHeight: "100dvh",
        color: "var(--brand-text)",
      }}
    >
      <div className="flex-1 min-h-0 flex flex-col z-20 px-5 py-5">
        {/* Back button — shown when onBack is provided */}
        {onBack && !isLoading && (
          <button
            onClick={onBack}
            className="absolute top-6 left-5 z-30 flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            aria-label="Go back"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium">Back</span>
          </button>
        )}

        {/* Header with wordmark */}
        <header className="flex flex-col justify-center items-center pt-8 z-20 px-5 pb-8 h-[200px] flex-1">
          <img src={Wordmark} alt="Murmurations" className="w-full max-w-[300px]" style={{ filter: "drop-shadow(0 2px 12px rgba(0,0,0,0.35))" }} />
          <p className="text-white/80 text-sm mt-3 tracking-[0.08em]">Stories of Migration in LA</p>
        </header>

        {/* XR error guidance replaces the spinner when present. */}
        {errorMessage ? (
          <div className="flex items-center justify-center flex-1">
            <div
              className="text-2xl short:text-xl text-center font-light animate-fade-in pb-8 short:pb-4 text-white"
              dangerouslySetInnerHTML={{ __html: errorMessage }}
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-6 py-16">
            <Spinner size="2md" variant="light" />
            {autoStart && !isLoading && <p className="text-white/70 text-sm">Starting AR...</p>}
            {progressLabel && (
              <div className="w-48 max-w-[60vw] flex flex-col items-center gap-1.5 -mt-2">
                {progressFraction != null && (
                  <div className="h-1 w-full rounded-full bg-white/20 overflow-hidden">
                    <div className="h-full bg-white/80" style={{ width: `${Math.round(progressFraction * 100)}%`, transition: "width 200ms linear" }} />
                  </div>
                )}
                <p className="text-white/45 text-xs">{progressLabel}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
